import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

interface BarChartCellProps {
  value: number;
  maxValue: number;
  color?: string;
  name: string;
  showLabel?: boolean;
  fullWidthBar?: boolean;
  showTooltip?: boolean;
  theme?: string;
  isFirst?: boolean;
}

const BarChartCell: React.FC<BarChartCellProps> = ({
  value,
  maxValue,
  name,
  showLabel,
  fullWidthBar = false,
  showTooltip = true,
  theme = 'dark',
  isFirst = 0,
}) => {
  const loaded = '#6E87FF';
  const released = '#00D0DB';
  const produced = '#9F75DF';

  let actualColor = loaded;
  if (name === 'released') actualColor = released;
  else if (name === 'produced') actualColor = produced;

  const options: Highcharts.Options = {
    chart: {
      type: 'bar',
      height: 48,
      backgroundColor: 'transparent',
      margin: [0, 0, 0, 0],
    },
    title: {
      text: `${value}`,
      style: {
        fontSize: '0px',
        height: 0,
      },
      margin: 0
    },
    xAxis: {visible: false},
    yAxis: {
      min: 0,
      max: maxValue,
      visible: false,
    },
    series: [
      {
        type: 'bar',
        data: [
          {
            y: value,
            dataLabels: showLabel
              ? {
                  enabled: true,
                  useHTML: true,
                  inside: false,
                  formatter: function () {
                    const context = this as any;
                    const label = context?.y?.toString() ?? '';
                    const charWidth = 8;
                    const labelPadding = 10;
                    const labelWidth = label.length * charWidth + labelPadding;

                    // Estimate bar width in pixels based on chart width and value proportion
                    const chartWidth = context.series.chart.plotWidth ?? 100;
                    const valueRatio = context.y / context.series.yAxis.max;
                    const estimatedBarWidth = chartWidth * valueRatio;

                    const fitsInside = estimatedBarWidth >= labelWidth * 4;
                    const themeStyle =
                      theme === 'light'
                        ? 'highcharts-label-outside-light'
                        : 'highcharts-label-outside-dark';

                    let className = fitsInside
                      ? 'highcharts-label-inside'
                      : themeStyle;

                    if (!isFirst && fitsInside) {
                      className += ' highcharts-label-with-margin';
                    }

                    // Dynamically set alignment/offset
                    context.align = fitsInside ? 'left' : 'right';
                    context.x = fitsInside ? 5 : 6;

                    return `<span class="${className}">${label}</span>`;
                  },
                  style: {
                    fontSize: '14px',
                    fontFamily: 'Inter',
                    fontWeight: '600',
                    textOutline: 'none',
                    textShadow: 'none',
                  },
                }
              : undefined,
          },
        ],
        showInLegend: false,
        color: actualColor,
        cursor: 'default',
      },
    ],
    tooltip: {
      enabled: showTooltip,
      formatter: function () {
        return `<b>${value} Docs</b>`;
      },
    },
    plotOptions: {
      bar: {
        pointWidth: fullWidthBar ? undefined : 10,
        borderWidth: 1,
      },
      series: {
        animation: false,
        borderColor: actualColor,
        enableMouseTracking: true,
      },
    },
    credits: {enabled: false},
    navigation: {
      buttonOptions: {
        enabled: false,
      },
    },
  };

  return <HighchartsReact highcharts={Highcharts} options={options} />;
};

export default BarChartCell;
